import { useCallback, useEffect, useMemo, useState } from "react";
import { useToasts } from "../../contexts/toasts";
import { Effort, EffortType } from "../../types/Entities";
import { Entities } from "../../api/Entities";
import { Alert, Button, Container, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faPen,
  faPlus,
  faSave,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { TailSpin } from "react-loader-spinner";
import {
  ConfigProvider,
  Empty,
  Form,
  GetProp,
  Input,
  Select,
  Space,
  Table,
} from "antd";
import IconButton from "../../common/IconButton";
import { debounce } from "lodash";
import AppSearch from "../../common/AppSearch";
import { PagedResults } from "../../types/generics";
import ListPageHeader from "../../common/ListPageHeader";
import ListPanel from "../../common/ListPanel";

export default function EffortsList() {
  const { showToast } = useToasts();
  const [efforts, setEfforts] = useState<Effort[]>();
  const [effortTypes, setEffortTypes] = useState<EffortType[]>([]);
  const [error, setError] = useState(false);
  const [totalEfforts, setTotalEfforts] = useState<number>();
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<{
    pageSize: number;
    currentPage: number;
    freeText: string;
    effortTypeId?: number;
  }>({
    pageSize: 20,
    currentPage: 1,
    freeText: "",
  });
  const [addModal, setAddModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [form] = Form.useForm();
  const [selectedEffort, setSelectedEffort] = useState<Effort>();

  // 🔹 Inizializza lista Efforts
  const initEfforts = useCallback(() => {
    const urlParams = new URLSearchParams();
    urlParams.append("page", `${filters.currentPage}`);
    urlParams.append("size", `${filters.pageSize}`);
    if (filters.freeText) urlParams.append("freeText", filters.freeText);
    if (filters.effortTypeId)
      urlParams.append("effortTypeId", `${filters.effortTypeId}`);

    Entities.getPaginatedEfforts(urlParams).then(
      ({ data }) => {
        setEfforts((data as PagedResults<Effort>).items);
        setTotalEfforts((data as PagedResults<Effort>).totalItems);
      },
      () => {
        showToast(
          "efforts-error",
          "Impossibile ottenere la lista degli Effort",
          "",
          "danger"
        );
        setEfforts([]);
        setError(true);
      }
    );
  }, [filters, showToast]);

  // 🔹 Inizializza EffortTypes per i filtri e il form
  const initEffortTypes = useCallback(() => {
    Entities.getEffortTypes().then(
      ({ data }) => {
        setEffortTypes(data.list);
      },
      () => {
        showToast(
          "efforttypes-error",
          "Impossibile ottenere la lista dei tipi di Effort",
          "",
          "danger"
        );
        setEffortTypes([]);
      }
    );
  }, [showToast]);

  const reset = useCallback(() => {
    setError(false);
    setEfforts(undefined);
    initEfforts();
    initEffortTypes();
  }, [initEfforts, initEffortTypes]);

  useEffect(() => {
    reset();
  }, [filters, reset]);

  const handlePageChange = useCallback((page: number) => {
    setFilters((prev) => ({ ...prev, currentPage: page }));
  }, []);

  const handlePageSizeChange = useCallback((_current: number, size: number) => {
    setFilters((prev) => ({ ...prev, pageSize: size, currentPage: 1 }));
  }, []);

  const handleEditClick = useCallback(
    (effort?: Effort) => () => {
      setSelectedEffort(effort);
      if (effort) {
        form.setFieldsValue({
          effort: effort.effort,
          effortTypeId: effort.effortTypeId,
        });
      }
      setAddModal(true);
    },
    [form]
  );

  const handleDeleteClick = useCallback(
    (effort: Effort) => () => {
      setSelectedEffort(effort);
      setDeleteModal(true);
    },
    []
  );

  const handleFilterChange = useCallback((text: string) => {
    setFilters((prev) => ({ ...prev, currentPage: 1, freeText: text }));
  }, []);

  const debounceSearchChange = useMemo(
    () => debounce(handleFilterChange, 500),
    [handleFilterChange]
  );

  useEffect(() => {
    return () => debounceSearchChange.cancel();
  }, [debounceSearchChange]);

  const handleFieldChange = useCallback(
    (field: string) => (value: any) => {
      setFilters((prev) => ({ ...prev, [field]: value, currentPage: 1 }));
    },
    []
  );

  const renderToolbar = useCallback(() => {
    return (
      <ListPageHeader
        title="Effort"
        count={totalEfforts}
        actions={
          <button onClick={handleEditClick()} className="button-primary">
            <FontAwesomeIcon icon={faPlus} />
            <span className="d-none d-sm-inline ms-2">Aggiungi Effort</span>
          </button>
        }
      >
        <AppSearch
          placeholder="Cerca per nome..."
          onChange={debounceSearchChange}
        />
        <Select
          value={filters.effortTypeId}
          placeholder="Filtra per Effort Type..."
          allowClear
          onChange={handleFieldChange("effortTypeId")}
        >
          {effortTypes.map((type) => (
            <Select.Option key={type.id} value={type.id}>
              {type.effortType}
            </Select.Option>
          ))}
        </Select>
      </ListPageHeader>
    );
  }, [
    filters.effortTypeId,
    debounceSearchChange,
    handleFieldChange,
    handleEditClick,
    effortTypes,
    totalEfforts,
  ]);

  const renderEmpty: GetProp<typeof ConfigProvider, "renderEmpty"> = () => (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nessun Effort" />
  );

  const renderTable = useCallback(() => {
    if (error)
      return (
        <Alert variant="danger" className="py-2">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span className="ms-2">
            C'è stato un errore, per favore riprova più tardi
          </span>
        </Alert>
      );

    return (
      <Table
        size="middle"
        bordered
        dataSource={efforts}
        rowKey="id"
        locale={{ emptyText: renderEmpty() }}
        pagination={{
          pageSize: filters.pageSize,
          current: filters.currentPage,
          onChange: handlePageChange,
          onShowSizeChange: handlePageSizeChange,
          total: totalEfforts,
        }}
      >
        <Table.Column
          key="effort"
          dataIndex="effort"
          title="Effort"
          className="text-center col-pattern"
        />
        <Table.Column
          key="effortType"
          dataIndex="effortType"
          title="Effort Type"
          className="text-center col-pattern"
          render={(_val, record: Effort) =>
            record.effortType?.effortType || "-"
          }
        />
        <Table.Column
          key="actions"
          title="Azioni"
          className="text-center col-pattern"
          render={(_val, record: Effort) => (
            <Space size={1}>
              <IconButton tooltip="Modifica" onClick={handleEditClick(record)}>
                <FontAwesomeIcon icon={faPen} />
              </IconButton>
              <IconButton
                tooltip="Elimina"
                className="text-danger"
                onClick={handleDeleteClick(record)}
              >
                <FontAwesomeIcon icon={faTrash} />
              </IconButton>
            </Space>
          )}
        />
      </Table>
    );
  }, [
    efforts,
    error,
    filters,
    totalEfforts,
    handlePageChange,
    handlePageSizeChange,
    handleEditClick,
    handleDeleteClick,
  ]);

  const renderAsyncContent = useCallback(() => {
    return <ListPanel loading={!efforts}>{renderTable()}</ListPanel>;
  }, [efforts, renderTable]);

  const handleAddModalClose = useCallback(() => {
    setAddModal(false);
    form.resetFields();
    setSelectedEffort(undefined);
  }, [form]);

  const handleDeleteModalClose = useCallback(() => {
    setDeleteModal(false);
    setSelectedEffort(undefined);
  }, []);

  const handleSubmit = useCallback(
    (values: any) => {
      setLoading(true);
      const body = { ...values };
      const promise = selectedEffort
        ? Entities.updateEffort(selectedEffort.id!, body)
        : Entities.createEffort(body);

      promise.then(
        () => {
          setLoading(false);
          showToast(
            "effort-success",
            `Effort ${selectedEffort ? "modificato" : "creato"} correttamente`,
            undefined,
            "success"
          );
          handleAddModalClose();
          reset();
        },
        () => {
          setLoading(false);
          showToast(
            "effort-error",
            `Errore nella ${
              selectedEffort ? "modifica" : "creazione"
            } dell’Effort`,
            undefined,
            "danger"
          );
        }
      );
    },
    [selectedEffort, showToast, handleAddModalClose, reset]
  );

  const handleDelete = useCallback(() => {
    setLoading(true);
    Entities.deleteEffort(selectedEffort!.id!).then(
      () => {
        setLoading(false);
        handleDeleteModalClose();
        reset();
        showToast(
          "delete-effort-success",
          "Effort eliminato correttamente",
          undefined,
          "success"
        );
      },
      () => {
        setLoading(false);
        showToast(
          "delete-effort-error",
          "Errore nella cancellazione dell’Effort, per favore riprova",
          undefined,
          "danger"
        );
      }
    );
  }, [selectedEffort, showToast, handleDeleteModalClose, reset]);

  return (
    <Container fluid="md" className="mt-5 list-page-container">
      {renderToolbar()}
      {renderAsyncContent()}

      {/* MODALE AGGIUNTA / MODIFICA */}
      <Modal show={addModal} onHide={handleAddModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedEffort ? "Modifica" : "Nuovo"} Effort
          </Modal.Title>
        </Modal.Header>
        <Form form={form} onFinish={handleSubmit}>
          <Modal.Body>
            <Form.Item
              label="Effort"
              name="effort"
              rules={[{ required: true, message: "Campo obbligatorio" }]}
            >
              <Input className="w-100" />
            </Form.Item>
            <Form.Item
              label="Effort Type"
              name="effortTypeId"
              rules={[{ required: true, message: "Campo obbligatorio" }]}
            >
              <Select
                placeholder="Seleziona un Effort Type"
                options={effortTypes.map((et) => ({
                  value: et.id,
                  label: et.effortType,
                }))}
              />
            </Form.Item>
          </Modal.Body>
          <Modal.Footer className="justify-content-between">
            <Button
              onClick={handleAddModalClose}
              variant="light"
              size="sm"
              className="border me-2"
            >
              Annulla
            </Button>
            <Button variant="success" size="sm" type="submit">
              {loading ? (
                <TailSpin width={15} height={15} color="#9c1419" />
              ) : (
                <>
                  <FontAwesomeIcon icon={faSave} />
                  <span className="ms-2">Salva</span>
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* MODALE ELIMINAZIONE */}
      <Modal show={deleteModal} onHide={handleDeleteModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Elimina Effort</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Sei sicuro di voler cancellare l’Effort{" "}
            <strong>{selectedEffort?.effort}</strong>?
          </p>
          <Alert variant="warning">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span className="ms-2">
              Questa azione non può essere annullata.
            </span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            size="sm"
            className="border"
            onClick={handleDeleteModalClose}
          >
            Chiudi
          </Button>
          <Button variant="danger" size="sm" onClick={handleDelete}>
            {loading ? (
              <TailSpin width={20} height={20} />
            ) : (
              <span>Cancella</span>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
